import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import styles from './GiftWrapper.module.scss';
import bundleDetailsDownSvg from '../../assets/images/svg/chevron-down-big.svg';
import FreeGiftAOVItem from '../../blocks/pdpV2/FreeGiftAOV/FreeGiftAOVItem';
import FreeGiftAOVDescription from '../../blocks/pdpV2/FreeGiftAOV/FreeGiftAOVDescription';
import messages from './messages';
import { onChangeSKUSelectedForIndex } from '../../utils';
import {
  deleteProductFromProductsSaved,
  addProductInProductsSaved,
} from '../../utils/aov';
import { getProductsSpecsAndMaterials } from '../../slices/pdp';
import AOVVerticalCards from '../AOVVerticalCards';
import { getVariantsWithPercentDiscount } from '../../utils/variants';
import AOVModalFooter from '../../blocks/pdpV2/AOVModalFooter';
import useDrupalSettings from '../../hooks/useDrupalSettings';
import useDynamicPdpDrupalSettings from '../../hooks/useDynamicPdpDrupalSettings';
import { DYNAMIC_PAGE_TYPE } from '../../blocks/PromoBannerTracker/constants';

const GiftWrapper = (props) => {
  const { summaryDisclaimer } = props;
  const dispatch = useDispatch();
  const [selectedProducts, setSelectedProducts] = useState({});
  const [isGiftSelected, setIsGiftSelected] = useState([]);
  const [isBundleDetailsOpened, setIsBundleDetailsOpened] = useState(false);
  const [isEnhancedBundleDetailsOpened, setIsEnhancedBundleDetailsOpened] =
    useState(false);
  const [lineItems, setLineItems] = useState([]);
  const [quantitiesForProducts, setQuantitiesForProducts] = useState({});

  const {
    pdp: { type: pdpType },
  } = useDrupalSettings();
  const dynamicPDPAOVS = useDynamicPdpDrupalSettings();
  const regularPDPAOVS = useDrupalSettings();
  const { aovs } =
    pdpType === DYNAMIC_PAGE_TYPE ? dynamicPDPAOVS : regularPDPAOVS;
  const { selectedSKU } = useSelector((state) => state.pdp);
  const selectedSkuAov = aovs?.find((aov) => aov.targetSku === selectedSKU);
  const giftsAov = selectedSkuAov?.gifts;

  const { formatMessage, locale } = useIntl();
  const { options: giftItems, completeSet } = giftsAov;

  useEffect(() => {
    const defaultSelectedProducts = giftItems.reduce((prev, curr) => {
      const giftMasterVariant =
        curr.gift.variants.find((variant) => variant.masterVariant) ||
        curr.gift.variants[0];

      return {
        ...prev,
        [curr.gift.productId]: [giftMasterVariant?.sku],
      };
    }, {});
    setSelectedProducts(defaultSelectedProducts);
    const productKeys = giftItems.flatMap((item) => {
      if (item.enhancedProduct.productKey !== item.gift.productKey) {
        return [item.gift.productKey, item.enhancedProduct.productKey];
      }
      return [item.gift.productKey];
    });
    dispatch(getProductsSpecsAndMaterials(productKeys));
  }, [giftItems, dispatch]);

  const setSelectedSKUsForProducts = useCallback((selectedCompleteSetSkus) => {
    setSelectedProducts(selectedCompleteSetSkus);
  }, []);

  const onChangeSKUSelectedForIndexCallback = useCallback(
    ({ sku, index, productId }) => {
      onChangeSKUSelectedForIndex({
        sku,
        index,
        productId,
        selectedSKUsForProducts: selectedProducts,
        setSelectedSKUsForProducts,
      });
    },
    [selectedProducts, setSelectedSKUsForProducts],
  );

  const removeSelectedProducts = useCallback(({ productId, sku }) => {
    // Check if gift Item ID is present, if yes and if the giftItemId is already selected, remove it and select the enhanced product id
    const updatedSkus = (prevState) =>
      !prevState[productId]
        ? addProductInProductsSaved({
            state: prevState,
            productId,
            sku,
          })
        : deleteProductFromProductsSaved({
            state: prevState,
            productId,
          });
    setSelectedProducts(updatedSkus);
  }, []);

  const handleGiftSelectionChange = ({ giftItem = null, productId, sku }) => {
    if (giftItem) {
      removeSelectedProducts({
        productId: giftItem.productId,
        sku: giftItem.sku,
      });
    }
    removeSelectedProducts({ productId, sku });
  };

  const hasGiftItemToRemove = (itemToRemove, itemToBeAdded) =>
    selectedProducts[itemToRemove.productId] &&
    !selectedProducts[itemToBeAdded.productId]
      ? itemToRemove
      : null;

  const handleQuantityIncrement = useCallback((productId) => {
    setQuantitiesForProducts((prev) => ({
      ...prev,
      [productId]: (prev[productId] || 1) + 1,
    }));
  }, []);

  const handleQuantityDecrement = useCallback((productId) => {
    setQuantitiesForProducts((prev) => ({
      ...prev,
      [productId]: Math.max((prev[productId] || 1) - 1, 1),
    }));
  }, []);

  const quantitiesForProductsJSON = JSON.stringify(quantitiesForProducts);

  useEffect(() => {
    const quantitiesForProductsValue = JSON.parse(quantitiesForProductsJSON);
    if (Object.keys(selectedProducts)?.length) {
      const newLineItems = Object.keys(selectedProducts)
        .map((productId) => {
          const giftProduct = giftItems.find(
            (item) => item?.gift.productId === productId,
          );
          const enhancedProduct = giftItems.find(
            (item) => item?.enhancedProduct.productId === productId,
          );

          const giftItem =
            giftProduct?.gift || enhancedProduct?.enhancedProduct;

          const currentProduct =
            giftItem ??
            completeSet.find((item) => item.productId === productId);

          const selectedSKUsWithProductInfo = selectedProducts[productId].map(
            (selectedVariantSKU, index) => {
              const currentVariant = currentProduct.variants.find(
                (variant) => variant.sku === selectedVariantSKU,
              );

              const moreThanOneSelected =
                selectedProducts[productId].filter(
                  (selectedSkuForCurrentProduct) =>
                    selectedSkuForCurrentProduct === selectedVariantSKU,
                )?.length > 1;
              // if same variant was added 2 times
              // calculate the discounted price
              let currentVariantWithDiscountedPrice = 0;

              const quantity = quantitiesForProductsValue[productId] || 1;

              if (moreThanOneSelected) {
                currentVariantWithDiscountedPrice =
                  index === 0
                    ? getVariantsWithPercentDiscount(
                        [currentVariant],
                        currentProduct.secondItemDiscount
                          ?.percentageFirstProduct || 0,
                      )[0]
                    : getVariantsWithPercentDiscount(
                        [currentVariant],
                        currentProduct.secondItemDiscount?.percentage || 0,
                      )[0];
              } else {
                currentVariantWithDiscountedPrice = currentVariant;
              }

              return {
                sku: selectedVariantSKU,
                productName: currentProduct.productName?.[locale],
                discountedPrice: currentVariantWithDiscountedPrice?.price,
                price: currentVariantWithDiscountedPrice?.discount
                  ? {
                      ...currentVariantWithDiscountedPrice?.price,
                      centAmount:
                        currentVariantWithDiscountedPrice?.price.centAmount +
                        currentVariantWithDiscountedPrice?.discount.centAmount,
                    }
                  : currentVariantWithDiscountedPrice?.price,
                quantity: Math.max(quantity, currentProduct.maxQuantity ?? 1),
              };
            },
          );

          return selectedSKUsWithProductInfo;
        })
        ?.reduce((acc, curr) => [...acc, ...curr], []);

      setLineItems(newLineItems);
    } else {
      setLineItems([]);
    }
  }, [
    selectedProducts,
    giftItems,
    quantitiesForProductsJSON,
    completeSet,
    locale,
  ]);

  useEffect(() => {
    const productIds = Object.keys(selectedProducts)
      .map((productId) => productId)
      .filter(Boolean);
    setIsGiftSelected(productIds);
  }, [selectedProducts]);

  return (
    <div className={styles['content-wrapper']}>
      <div className={styles.content}>
        <div className={styles['bundle-container']}>
          {giftItems.map((item) => {
            if (item?.gift?.variants?.length > 0) {
              return (
                <>
                  <div
                    className={classNames(styles['gift-item-container'], {
                      [styles.selected]: isGiftSelected?.includes(
                        item.gift.productId,
                      ),
                    })}
                    key={item.gift.productId}
                  >
                    <button
                      type='button'
                      className={classNames(styles['bundle-info-section'], {
                        [styles.select]: !isGiftSelected?.includes(
                          item.gift.productId,
                        ),
                      })}
                      onClick={() =>
                        handleGiftSelectionChange({
                          giftItem: hasGiftItemToRemove(
                            item.enhancedProduct,
                            item.gift,
                          ),
                          productId: item.gift.productId,
                          sku: item.gift.variants?.[0]?.sku,
                        })
                      }
                    >
                      <img
                        src={item?.gift?.variants?.[0]?.image}
                        alt={item.gift?.productName[locale]}
                        className={styles['bundle-image']}
                      />
                      <div className={styles['bundle-info']}>
                        <div className={styles['bundle-info-top-section']}>
                          <div className={styles['bundle-badge']}>
                            {formatMessage(messages.freeGift)}
                          </div>
                          <div
                            className={classNames(styles['bundle-check-mark'], {
                              [styles.selected]: isGiftSelected?.includes(
                                item.gift.productId,
                              ),
                              [styles.select]: !isGiftSelected?.includes(
                                item.gift.productId,
                              ),
                            })}
                          />
                        </div>
                        <FreeGiftAOVDescription item={item.gift} />
                      </div>
                    </button>
                    <div
                      key={item.gift.productKey}
                      className={classNames(styles['bundle-bottom-section'], {
                        [styles['bundle-details-opened']]:
                          isBundleDetailsOpened,
                      })}
                    >
                      <button
                        type='button'
                        className={classNames(
                          styles['bundle-details-toggle-button'],
                        )}
                        onClick={() =>
                          setIsBundleDetailsOpened((prev) => !prev)
                        }
                      >
                        <span>{formatMessage(messages.productdetails)}</span>
                        <img
                          src={bundleDetailsDownSvg}
                          alt={formatMessage(messages.toggleProductDetailsAlt)}
                        />
                      </button>
                      <div className={styles['bundle-items']}>
                        <FreeGiftAOVItem
                          item={item.gift}
                          selectedSku={selectedProducts[item.gift.productKey]}
                        />
                      </div>
                    </div>
                  </div>
                  {item?.enhancedProduct.productKey !==
                    item.gift.productKey && (
                    <div
                      className={classNames(styles['gift-item-container'], {
                        [styles.selected]: isGiftSelected?.includes(
                          item.enhancedProduct.productId,
                        ),
                      })}
                      key={item.enhancedProduct.productId}
                    >
                      <button
                        type='button'
                        className={classNames(styles['bundle-info-section'], {
                          [styles.select]: !isGiftSelected?.includes(
                            item.enhancedProduct.productId,
                          ),
                        })}
                        onClick={() =>
                          handleGiftSelectionChange({
                            giftItem: hasGiftItemToRemove(
                              item.gift,
                              item.enhancedProduct,
                            ),
                            productId: item.enhancedProduct.productId,
                            sku: item.enhancedProduct.variants?.[0]?.sku,
                          })
                        }
                      >
                        <img
                          src={item?.enhancedProduct?.variants?.[0]?.image}
                          alt={item.enhancedProduct?.productName[locale]}
                          className={styles['bundle-image']}
                        />
                        <div className={styles['bundle-info']}>
                          <div className={styles['bundle-info-top-section']}>
                            <div className={styles['bundle-badge-enhanced']}>
                              {formatMessage(messages.freeGiftEnhanced)}
                            </div>
                            <div
                              className={classNames(
                                styles['bundle-check-mark'],
                                {
                                  [styles.selected]: isGiftSelected?.includes(
                                    item.enhancedProduct.productId,
                                  ),
                                  [styles.select]: !isGiftSelected?.includes(
                                    item.enhancedProduct.productId,
                                  ),
                                },
                              )}
                            />
                          </div>
                          <FreeGiftAOVDescription item={item.enhancedProduct} />
                        </div>
                      </button>
                      <div
                        key={item.enhancedProduct.productKey}
                        className={classNames(styles['bundle-bottom-section'], {
                          [styles['bundle-details-opened']]:
                            isEnhancedBundleDetailsOpened,
                        })}
                      >
                        <button
                          type='button'
                          className={classNames(
                            styles['bundle-details-toggle-button'],
                          )}
                          onClick={() =>
                            setIsEnhancedBundleDetailsOpened((prev) => !prev)
                          }
                        >
                          <span>{formatMessage(messages.productdetails)}</span>
                          <img
                            src={bundleDetailsDownSvg}
                            alt={formatMessage(
                              messages.toggleProductDetailsAlt,
                            )}
                          />
                        </button>
                        <div className={styles['bundle-items']}>
                          <FreeGiftAOVItem
                            item={item.enhancedProduct}
                            selectedSku={
                              selectedProducts[item.enhancedProduct.productKey]
                            }
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </>
              );
            }
            return null;
          })}
          {/* Complete Your Set */}
          {completeSet?.length ? (
            <div className={styles['complete-set-container']}>
              <span className={styles.headline}>
                {formatMessage(messages.completeYourSetTitle)}
              </span>
              <AOVVerticalCards
                aovType='completeSet'
                filteredItems={completeSet}
                quantitiesForProducts={quantitiesForProducts}
                onQuantityIncrement={handleQuantityIncrement}
                onQuantityDecrement={handleQuantityDecrement}
                onChangeSKUSelectedForIndex={
                  onChangeSKUSelectedForIndexCallback
                }
                selectedSKUsForProducts={selectedProducts}
              />
            </div>
          ) : null}
        </div>
      </div>
      {summaryDisclaimer?.[locale]?.trim() && (
        <div className={styles['summary-disclaimer']}>
          {summaryDisclaimer[locale]}
        </div>
      )}
      <AOVModalFooter lineItems={lineItems} />
    </div>
  );
};

GiftWrapper.propTypes = {
  summaryDisclaimer: PropTypes.objectOf(PropTypes.string),
};

export default GiftWrapper;
